import { Box, Button, Chip, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import { Briefcase, Calendar, CheckCircle, Mail, MapPin, MessageSquare } from 'lucide-react';
import React from 'react';

export interface IEmployeeCardData {
    id: number;
    title: string;
    firstName?: string;
    lastName?: string;
    pictureId: string | null;
    email: string;
    jobTitle?: string;
    availability?: number;
    skills?: Array<{ title: string; level?: number }>;
    projects?: Array<{ title: string; active?: boolean }>;
    location?: string;
    role?: string;
}

export interface IEmployeeCardConfig {
    variant: 'team' | 'project-match';
    showAvailability?: boolean;
    showSkills?: boolean;
    showProjects?: boolean;
    showLocation?: boolean;
    maxSkills?: number;
    primaryAction?: {
        label: string;
        onClick: (employee: IEmployeeCardData) => void;
        variant?: 'contained' | 'outlined';
    };
    secondaryAction?: {
        label: string;
        onClick: (employee: IEmployeeCardData) => void;
        icon?: React.ReactNode;
    };
}

interface EmployeeCardProps {
    employee: IEmployeeCardData;
    config: IEmployeeCardConfig;
    featureFlags?: { messages?: boolean };
}

export default function EmployeeCard({ employee, config, featureFlags }: EmployeeCardProps) {
    const theme = useTheme();

    const getAvailabilityColor = (availability: number) => {
        return availability >= 60
            ? theme.palette.success.main
            : availability >= 30
              ? theme.palette.warning.main
              : theme.palette.error.main;
    };

    const getAvailabilityStatus = (availability: number) => {
        return availability >= 60 ? 'Verfügbar' : availability >= 30 ? 'Begrenzt verfügbar' : 'Ausgelastet';
    };

    const renderAvailability = () => {
        if (!config.showAvailability || !employee.availability) return null;

        return (
            <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Verfügbarkeit
                    </Typography>
                    <Chip
                        label={getAvailabilityStatus(employee.availability)}
                        size="small"
                        sx={{
                            bgcolor: alpha(getAvailabilityColor(employee.availability), 0.1),
                            color: getAvailabilityColor(employee.availability),
                            fontWeight: 500,
                            fontSize: '0.75rem',
                        }}
                    />
                </Stack>
                <LinearProgress
                    variant="determinate"
                    value={employee.availability}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.divider, 0.2),
                        '& .MuiLinearProgress-bar': {
                            bgcolor: getAvailabilityColor(employee.availability),
                            borderRadius: 3,
                        },
                    }}
                />
            </Box>
        );
    };

    const renderSkills = () => {
        if (!config.showSkills || !employee.skills?.length) return null;

        const skillsToShow = employee.skills.slice(0, config.maxSkills || 3);

        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Top Skills
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                    {skillsToShow.map((skill, index) => (
                        <Chip
                            key={index}
                            label={skill.title}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                fontSize: '0.75rem',
                            }}
                        />
                    ))}
                    {employee.skills.length > (config.maxSkills || 3) && (
                        <Chip
                            label={`+${employee.skills.length - (config.maxSkills || 3)}`}
                            size="small"
                            variant="outlined"
                            sx={{ opacity: 0.7, fontSize: '0.75rem' }}
                        />
                    )}
                </Stack>
            </Box>
        );
    };

    const renderProjects = () => {
        if (!config.showProjects || !employee.projects?.length) return null;

        const activeProject = employee.projects.find((p) => p.active) || employee.projects[0];

        return (
            <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Briefcase size={14} color={theme.palette.text.secondary} />
                    <Typography variant="caption" color="text.secondary">
                        {activeProject.title}
                    </Typography>
                    {activeProject.active && <CheckCircle size={12} color={theme.palette.success.main} />}
                </Stack>
            </Box>
        );
    };

    const renderLocation = () => {
        if (!config.showLocation || !employee.location) return null;

        return (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <MapPin size={14} color={theme.palette.text.secondary} />
                <Typography variant="caption" color="text.secondary">
                    {employee.location}
                </Typography>
            </Stack>
        );
    };

    return (
        <Box
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4],
                },
            }}
        >
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <EmployeePicture
                    employee={{
                        id: employee.id,
                        firstName: employee.firstName || employee.title.split(' ')[0] || '',
                        lastName: employee.lastName || employee.title.split(' ').slice(-1)[0] || '',
                        pictureId: employee.pictureId,
                    }}
                    avatarSize={56}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {employee.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {employee.jobTitle || employee.role || 'Teammitglied'}
                    </Typography>
                </Box>
            </Stack>

            {/* Content */}
            {renderAvailability()}
            {renderSkills()}
            {renderProjects()}
            {renderLocation()}

            {/* Actions */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                {config.primaryAction && (
                    <Button
                        variant={config.primaryAction.variant || 'contained'}
                        size="small"
                        onClick={() => config.primaryAction!.onClick(employee)}
                        sx={{ flex: 1 }}
                    >
                        {config.primaryAction.label}
                    </Button>
                )}
                {config.secondaryAction && featureFlags?.messages && (
                    <IconButton
                        size="small"
                        onClick={() => config.secondaryAction!.onClick(employee)}
                        sx={{
                            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                            },
                        }}
                    >
                        <Mail size={16} />
                    </IconButton>
                )}
            </Stack>
        </Box>
    );
}
